/* =============================================================================

PURPOSE:  Stores invitation to NEW and EXISTING users

DB:       Realtime DB

PATH:     /invitations/{receiverEmailAddress}/... (new users)
          /user/{usedId}/invitations (existing users)

  New Users:
    /invitations/{email}/{senderId}/visionboard/{visionboardID} = invitation
    /invitations/{email}/{senderId}/challenge/{challengeId} = invitation
    /invitations/{email}/{senderId}/sequence/{sequenceId} = invitation
    /invitations/{email}/{senderId}/goal/{goalId} = invitation
    /invitations/{email}/{senderId}/app/ = invitation

  Existing Users:
    /user/{userId}/invitations/visionboard/{visionboardID} = invitation
    /user/{userId}/invitations/challenge/{challengeId} = invitation
    /user/{userId}/invitations/sequence/{sequenceId} = invitation
    /user/{userId}/invitations/goal/{goalId} = invitation
    (No app invitation, since user is already user of the app)

  or

  /invitation/{email}/{senderId}/*
  /user/{userId}/invitation_outgoing/*
    { email: true }

  /user/{receiverId}/invitations/{senderId}/*
  /user/{senderId}/invitation_outgoing/*
    { email: true }

  Format:
    /invitations/{inviteId}/content/visionboard/{visionboardID} = true or cachedName
    /invitations/{inviteId}/content/goal/{goalId} = true or cachedName
    /invitations/{inviteId}/content/challenge/{challengeId} = true or cachedName

REQUIREMENTS:

Invitations:

  1. Users must be able to find existing users
  2. Users must be able to send invitation to new users
  3. Users must be able to send invitation to new existing users
  4. Users can be invited by different users
  5. Users can be invited to different goals/visionboards/...
  6. Users can see/list their invitations
  7. Users can revoke invitations
  8. Existing users can accept or decline invitations
  9. Others users should be able to see invitation status
  10. New users will get an invitation link which is used after sign up

  ? 11. Users should be able to ask for invitation

Sign up / Accept:

  1. Permissions are granted to existing users who accept invitations
  2. When EXISTING user accepts EXISTING user should
    - get message
    - invitation should disappear
  3. When NEW user joins NEW user should
    - get access permission to existing valid invitations
    - get permissions
    - have inviting users as friends
  4. When NEW user join EXISTING user should
    - get reward
    - get message
    - get email
    - have new user as friend

  Security / Edge cases:

    1. It should NOT be possible to edit existing invitations
    2. It should NOT be possible to change Ids after invitations has been created
    3. Users can only see their own sent invitations
    4. Users can only see their own received invitations
    5. Users can change their email address
    6. Users can delete their account
    7. User can sign up with anothers persons email address

  Contacts:

    1. Users can send friend request
    2. Users can see their friend request
    3. Users can revoke friend request
    4. Users can accept or decline friend request
    5. Accepted friend request add users to each others contact list

  Msginbox:

    1. Users should see their own messages
    2. Other users should be able to write messages to msginbox, but not read
    3. Users can delete messages in their inbox
    4. Messages get automatically deleted after 7 days
    ? Users can edit / delete their own messages send to other users

============================================================================= */

import moment from '@/moment-gp'

export default {
  create({
    isNewUser,
    type,
    senderUserId,
    senderName,
    receiverEmail,
    receiverUserId
  }, data) {

    let invitation = {
      /* When was the invitation created */
      invitedAt:        moment.currentTimeServer('REALTIMEDB'),

      /* Who created the invitation */
      senderUserId:     senderUserId, // stored in key

      /*
      Cached name of the sender, so a name can be displayed without loading
      the user */
      cachedSenderName: senderName, // || firstName + " " + lastName,

      // cachedSenderPicture: senderPicture,

      /*
      Current status of the invitation
        0 = waiting
        1 = accepted
        2 = declined
        3 = revoked (same as just deleted?)
        11 = accepted and mark for deletion
      */
      status:           0, // Doppelt? nur existing user?

      /* Personal messaged added by the sending user */
      personalMessage:  data.personalMessage || '',

      /* Should a copy of the invitation be send to original user */
      sendCopy:         data.sendCopy || false,

      /*
      Content to which to user was invited see below
      No or empty content means, that user was invited to the Goalpilot App.
      */
      appContent:       {},
    };

    if ( isNewUser ) {

      Object.assign(invitation, {
        /* How many times has the user been contacted by email */
        numMailsSent:       0,

        /* How many times had the mail bounced */
        numMailBounces:     0,

        /* Was the user converted? */
        userActivated:      data.userActivated || false,

        /* This is special field that new users can write to.
           A cloud function listens for changes of this value */
        clientActivationRequest: 0, // Must be 0, otherwise Cloud function might get triggered

        /* When user logs in the first time, should we grand permissions to
           the goal/visionboard/challenge/etc. when creating the user? */
        permissionGranted:  data.permissionGranted || false, // 1 = no, 2 = in progress, 3 = yes

        /* Which permission level should the new user get
           0 = none, 1 = viewer, 2 = editor, etc. */
        permissionLevel:    data.permissionLevel || 1,

        /* Receivers email address */
        receiverEmail:      receiverEmail, // stored in key
      })

    } else {
      Object.assign(invitation, {
        receiverUserId:     receiverUserId, // stored in key
      })
    }

    switch ( type ) {

      // Visionboard inivtation
      case 'Visionboard':
        Object.assign(invitation.appContent, {
          type:          'Visionboard',
          visionboardId: data.visionboarId  || 0,
          cachedName:    data.cachedName    || 'MY VISIONBOARD',
          cachedPicture: data.cachedPicture || '',
        })
      break;

      // Goal invitation
      case 'Goal':
        Object.assign(invitation.appContent, {
          type:          'Goal',
          goalId:        data.goalId        || 0,
          cachedName:    data.cachedName    || 'MY GOAL',
          cachedPicture: data.cachedPicture || '',
        })
      break;

      // Challenge inivtation
      case 'Challenge':
        Object.assign(invitation.appContent, {
          type:          'Challenge',
          challengeId:   data.challengeId   || 0,
          cachedName:    data.cachedName    || 'MY CHALLENGE',
          cachedPicture: data.cachedPicture || '',
        })
      break;

      // Sequence invitation
      case 'Sequence':
        Object.assign(invitation.appContent, {
          type:          'Sequence',
          sequenceId:    data.sequenceId    || 0,
          cachedName:    data.cachedName    || 'MY SEQUENCE',
          cachedPicture: data.cachedPicture || '',
        })
      break;

      // Goalpilot invitation = Contact Request
      default:
        Object.assign(invitation.appContent, {
          type:          'Goalpilot',
        })
      break;

      // Contact "invitation"?
    }

    return invitation
  },

  fields: {
    invitedAt: { validate_bolt_type: 'ServerTimestamp' },
    senderUserId: { validate_bolt_type: 'UserID' },
    cachedSenderName: { validate_bolt_type: 'String' },
    status: { validate_bolt_type: 'Number' },
    personalMessage: { validate_bolt_type: 'String' },
    sendCopy: { validate_bolt_type: 'Boolean' },
    appContent: { validate_bolt_type: 'InvitationAppContent' },
    // ---
    'appContent/type': { validate_bolt_type: 'String' },
    'appContent/cachedName': { validate_bolt_type: 'String' },
    'appContent/cachedPicture': { validate_bolt_type: 'String' },
    'appContent/visionboardId': { validate_bolt_type: 'AnyID' },
    'appContent/goalId': { validate_bolt_type: 'AnyID' },
    'appContent/challengeId': { validate_bolt_type: 'AnyID' },
    'appContent/sequenceId': { validate_bolt_type: 'AnyID' },
    // ---
    numMailsSent: { validate_bolt_type: 'Number' },
    numMailBounces: { validate_bolt_type: 'Number' },
    userActivated: { validate_bolt_type: 'Boolean' },
    permissionGranted: { validate_bolt_type: 'Boolean' },
    permissionLevel: { validate_bolt_type: 'Number' },
    receiverEmail: { validate_bolt_type: 'EMail' },
    // ---
    clientActivationRequest: { validate_bolt_type: 'ServerTimestamp' },
    // ---
    receiverUserId: { validate_bolt_type: 'UserID' },
    awsMessageId: {}, // Used by cloud function
  }
}
